<template>
	<article
		:data-id="resource.ID"
		:class="{
			'resource-has-series': resource.order,
			'resource-small': small,
			'resource-current': small,
		}"
	>
		<div class="article-inner">
			<router-link
				:to="{
					name: 'resourceSingle',
					params: {
						slug: resource.post_name,
					},
				}"
				class="hero no-style"
			>
				<div
					class="aspect-ratio-box"
					:style="{
						'background-image': wrapBackgroundImageUrlToCss(getHeroUrl(resource)),
					}"
				>
					<ResourceAudienceComponent :audience="resource.audience" />
					<ResourceTypeComponent :type="resource.type" />
				</div>
				<div class="current">Current</div>
			</router-link>
			<div class="article-content">
				<div class="title-container">
					<div class="title">
						<ResourceSeriesComponent :resource="resource" />
						<router-link
							:to="{
								name: 'resourceSingle',
								params: {
									slug: resource.post_name,
								},
							}"
							class="no-style"
						>
							{{ resource.post_title }}
						</router-link>
					</div>
					<div
						v-if="!hideActions"
						class="actions"
					>
						<ResourceRecommendComponent
							v-if="resource.type !== 'bot'"
							:resource="resource"
						/>
						<ResourceFavouriteComponent :resource="resource" />
					</div>
				</div>

				<div
					v-if="resource.post_excerpt"
					class="excerpt"
				>
					{{ resource.post_excerpt }}
				</div>

				<AuthorisationComponent
					v-if="false"
					:in-pil="true"
				>
					<div>Sort: {{ resource.sort }}</div>
					<div>{{ resource.sortData }}</div>

					<a
						:href="resource.edit_link"
						target="_blank"
						>edit</a
					>

					<div v-if="resource.categories.length">
						cats:
						<ul>
							<li
								v-for="c in resource.categories"
								:key="c.term_id"
								@click="handleCategoryFilterUpdate(c.slug)"
							>
								{{ c.name }}
							</li>
						</ul>
					</div>
				</AuthorisationComponent>
			</div>
		</div>
	</article>
</template>

<script lang="ts">
import { defineComponent, type PropType } from 'vue';
import { useResourceStore } from '@/stores/resourceStore';
import { mapActions } from 'pinia';
import AuthorisationComponent from '@/components/shared/AuthorisationComponent.vue';
import ResourceFavouriteComponent from '@/components/resources/ResourceFavouriteComponent.vue';
import ResourceRecommendComponent from '@/components/resources/ResourceRecommendComponent.vue';
import ResourceTypeComponent from '@/components/resources/ResourceTypeComponent.vue';
import ResourceAudienceComponent from '@/components/resources/ResourceAudienceComponent.vue';
import ResourceSeriesComponent from '@/components/resources/ResourceSeriesComponent.vue';
import type { Resource } from '@/interfaces/resourceInterface';
import { getHeroUrl, wrapBackgroundImageUrlToCss } from '@/helpers';

export default defineComponent({
	components: { AuthorisationComponent, ResourceFavouriteComponent, ResourceTypeComponent, ResourceAudienceComponent, ResourceRecommendComponent, ResourceSeriesComponent },
	props: {
		resource: {
			type: Object as PropType<Resource>,
			default: null,
		},
		hideActions: {
			type: Boolean,
			default: false,
		},
		small: {
			type: Boolean,
			default: false,
		},
		current: {
			type: Boolean,
			default: false,
		},
	},
	methods: {
		wrapBackgroundImageUrlToCss,
		getHeroUrl,
		...mapActions(useResourceStore, ['handleFilterInputUpdate', 'toggleResourceFavourite']),
	},
});
</script>

<style scoped lang="scss">
@use '@/styles/utils';

article {
	container: resource-list-item / inline-size;
	font-size: 1rem;

	.article-inner {
		display: flex;
		flex-direction: column;
		gap: 16px;
	}

	.hero {
		position: relative;

		.aspect-ratio-box {
			padding-top: 56.25%;
		}

		.current {
			display: none;
			position: absolute;
			justify-content: center;
			align-items: center;
			inset: 0 0 0 0;
			background-color: rgba(0 0 0 / 40%);
			color: white;
			font-size: 1.2rem;
		}

		&.router-link-exact-active {
			.current {
				display: flex;
			}
		}
	}

	.article-content {
		flex: 1;

		.title-container {
			display: flex;
			align-items: start;
			margin: 0 0 3px 0;

			.resource-order {
				margin-bottom: -3px;
			}

			a {
				margin-right: auto;
				color: rgb(var(--rs-color-brand-cherry));
				font-weight: 600;
				font-size: 2.2em;
				font-family: var(--rs-typography-font-heading);
			}

			.recommend-container {
				margin-right: 5px;
				padding-top: 6px;
			}

			.actions {
				display: flex;
				justify-content: center;
				align-items: center;
				gap: 4px;
				margin-left: auto;
				padding-top: 0;
			}
		}

		.excerpt {
			font-size: 1.6em;
		}
	}

	@container resource-list-item (width >= 500px) {
		.article-inner {
			flex-direction: row;
		}

		.article-content {
			.title-container {
				.title {
					padding-top: 8px;
				}
			}
		}

		&.resource-has-series {
			.article-content {
				.title-container {
					.title {
						padding-top: 0;
					}
				}
			}
		}

		.hero {
			width: 200px;
		}
	}

	.resource-small {
		.article-inner {
			flex-direction: row;
		}

		.article-content {
			.title-container {
				.resource-order {
					margin-bottom: 0;
				}

				a {
					font-size: 2em;
					line-height: 1.15;
				}
			}

			.excerpt {
				display: none;
			}
		}

		.hero {
			width: 150px;

			.icon-container {
				display: none;
			}
		}
	}
}
</style>
